// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import * as AuthAPI from 'lib/api/auth';
import { applyPenders } from 'lib/common';

/* ACTION TYPE */
const SET_EMAIL_INPUT = 'auth/SET_EMAIL_INPUT';
const SET_PASSWORD_INPUT = 'auth/SET_PASSWORD_INPUT';
const SET_ORDER_ID_INPUT = 'auth/SET_ORDER_ID_INPUT';
const SET_ORDER_EMAIL_INPUT = 'auth/SET_ORDER_EMAIL_INPUT';
const EMAIL_LOGIN = 'auth/EMAIL_LOGIN';
const SEND_AUTH_EMAIL = 'auth/SEND_AUTH_EMAIL';
const RETRY_AUTH_EMAIL = 'auth/RETRY_AUTH_EMAIL';
const CHANGE_REGISTER_FORM = 'auth/CHANGE_REGISTER_FORM';
const CHANGE_RESET_FORM = 'auth/CHANGE_RESET_FORM';
const CHANGE_PASSWORD = 'auth/CHANGE_PASSWORD';
const GET_CODE = 'auth/GET_CODE';
const LOCAL_REGISTER = 'auth/LOCAL_REGISTER';
const CODE_LOGIN = 'auth/CODE_LOGIN';
const VERIFY_SOCIAL = 'auth/VERIFY_SOCIAL';
const SOCIAL_REGISTER = 'auth/SOCIAL_REGISTER';
const SOCIAL_LOGIN = 'auth/SOCIAL_LOGIN';
const AUTOCOMPLETE_REGISTER_FORM = 'auth/AUTOCOMPLETE_REGISTER_FORM';
const SET_ERROR = 'auth/SET_ERROR';
const SET_NEXT_URL = 'auth/SET_NEXT_URL';

const GET_PROVIDER_TOKEN = 'auth/GET_PROVIDER_TOKEN';
const SSO_LOGIN = 'auth/SSO_LOGIN';
const SSO_SIGNUP = 'auth/SSO_SIGNUP';
const SSO_LOGOUT = 'auth/SSO_LOGOUT';

/* ACTION CREATOR */
const setEmailInput = createAction(SET_EMAIL_INPUT, (value: string) => value);
const setPasswordInput = createAction(SET_PASSWORD_INPUT, (value: string) => value);
const setOrderIdInput = createAction(SET_ORDER_ID_INPUT, (value: string) => value);
const setOrderEmailInput = createAction(SET_ORDER_EMAIL_INPUT, (value: string) => value);

type EmailLoginPayload = { email: string, password: string, next?: ?string };
const emailLogin = createAction(
  EMAIL_LOGIN,
  AuthAPI.emailLogin,
  (payload: EmailLoginPayload) => payload,
);

type SendAuthEmailPayload = { email: string, next?: ?string };
const sendAuthEmail = createAction(
  SEND_AUTH_EMAIL,
  AuthAPI.sendAuthEmail,
  (payload: SendAuthEmailPayload) => payload,
);
const retryAuthEmail = createAction(RETRY_AUTH_EMAIL);
const getCode = createAction(GET_CODE, AuthAPI.getCode);

type ChangeRegisterFormPayload = { name: string, value: string };
const changeRegisterForm = createAction(
  CHANGE_REGISTER_FORM,
  (payload: ChangeRegisterFormPayload) => payload,
);
type ChangeResetFormPayload = { name: string, value: string };
const changeResetForm = createAction(
  CHANGE_RESET_FORM,
  (payload: ChangeResetFormPayload) => payload,
);

const changePassword = createAction(CHANGE_PASSWORD, AuthAPI.changePassword);

const localRegister = createAction(LOCAL_REGISTER, AuthAPI.localRegister);
const codeLogin = createAction(CODE_LOGIN, AuthAPI.codeLogin);
const verifySocial = createAction(VERIFY_SOCIAL, AuthAPI.verifySocial);
const socialRegister = createAction(SOCIAL_REGISTER, AuthAPI.socialRegister);
const socialLogin = createAction(SOCIAL_LOGIN, AuthAPI.socialLogin);

type ErrorType = { name: string, payload?: ?any };
const setError = createAction(SET_ERROR, (payload: ErrorType) => payload);

const setNextUrl = createAction(SET_NEXT_URL, (payload: string) => payload);

type AutoCompleteFormPayload = {
  email: string,
  name: string,
};
const autoCompleteRegisterForm = createAction(
  AUTOCOMPLETE_REGISTER_FORM,
  (payload: AutoCompleteFormPayload) => payload,
);

const getProviderToken = createAction(
  GET_PROVIDER_TOKEN,
  AuthAPI.getProviderToken,
  meta => meta.type,
);

const ssoLogin = createAction(SSO_LOGIN, AuthAPI.getSsoLoginUrl);
const ssoSignup = createAction(SSO_SIGNUP, AuthAPI.getSsoSignupUrl);
const ssoLogout = createAction(SSO_LOGOUT, AuthAPI.getSsoLogoutUrl);

/* ACTION FLOW TYPE */
type SetEmailInputAction = ActionType<typeof setEmailInput>;
type SetPasswordInputAction = ActionType<typeof setPasswordInput>;
type SetOrderIdInputAction = ActionType<typeof setOrderIdInput>;
type SetOrderEmailInputAction = ActionType<typeof setOrderEmailInput>;
type ReTryAuthEmailAction = ActionType<typeof retryAuthEmail>;
type ChangeRegisterFormAction = ActionType<typeof changeRegisterForm>;
type ChangeResetFormAction = ActionType<typeof changeResetForm>;
type AutoCompleteRegisterForm = ActionType<typeof autoCompleteRegisterForm>;
type SetErrorAction = ActionType<typeof setError>;
type SetNextUrlAction = ActionType<typeof setNextUrl>;
type GetProviderTokenResponseAction = {
  type: string,
  payload: {
    data: {
      token: string,
    },
  },
  meta: string,
};

/* ACTION CREATORS INTERFACE */
export interface AuthActionCreators {
  setEmailInput(value: string): any;

  setPasswordInput(value: string): any;

  setOrderIdInput(value: string): any;

  setOrderEmailInput(value: string): any;

  emailLogin(payload: EmailLoginPayload): any;

  sendAuthEmail(payload: SendAuthEmailPayload): any;

  retryAuthEmail(email: ?string): any;

  changeRegisterForm({ name: string, value: string }): any;

  changeResetForm({ name: string, value: string }): any;

  changePassword(password: string): any;

  getCode(code: string): any;

  localRegister(payload: AuthAPI.LocalRegisterPayload): any;

  codeLogin(code: string): any;

  verifySocial(payload: AuthAPI.VerifySocialPayload): any;

  socialRegister(payload: AuthAPI.SocialRegisterPayload): any;

  socialLogin(payload: AuthAPI.SocialLoginPayload): any;

  autoCompleteRegisterForm(payload: AutoCompleteFormPayload): any;

  setError(payload: ErrorType): any;

  setNextUrl(payload: string): any;

  getProviderToken(key: AuthAPI.GetProviderTokenPayload): any;

  ssoLogin(next?: ?string): any;

  ssoSignup(next?: ?string): any;

  ssoLogout(redirectUri?: ?string): any;
}

/* EXPORT ACTION CREATORS */
export const actionCreators: AuthActionCreators = {
  setEmailInput,
  setPasswordInput,
  setOrderIdInput,
  setOrderEmailInput,
  emailLogin,
  sendAuthEmail,
  retryAuthEmail,
  changeRegisterForm,
  changeResetForm,
  changePassword,
  getCode,
  localRegister,
  codeLogin,
  verifySocial,
  socialRegister,
  socialLogin,
  autoCompleteRegisterForm,
  setError,
  setNextUrl,
  getProviderToken,
  ssoLogin,
  ssoSignup,
  ssoLogout,
};

/* STATE TYPES */
export type SocialAuthResult = ?{
  provider: string,
  accessToken: string,
};

export type AuthResult = ?{
  user: {
    id: string,
    email: string,
    username: string,
    displayName: string,
    thumbnail?: ?string,
  },
  token: string,
};

export type VerifySocialResult = ?{
  id: string,
  thumbnail: ?string,
  email: ?string,
  name: ?string,
  exists: boolean,
};

export type Auth = {
  email: string,
  sentEmail: boolean,
  isUser: boolean,
  registerForm: {
    displayName: string,
    email: string,
    username: string,
    shortBio: string,
    isAllAgree: boolean,
    isAdult: boolean,
    isTerms: boolean,
    isPrivacy: boolean,
    isPromotion: boolean,
  },
  isSocial: boolean,
  registerToken: string,
  authResult: AuthResult,
  socialAuthResult: SocialAuthResult,
  verifySocialResult: VerifySocialResult,
  error: ?{
    name: string,
    payload: any,
  },
  nextUrl: ?string,
  tokenData: {
    type: ?string,
    token: ?string,
  },
};

/* INITIAL STATE */
const initialState: Auth = {
  email: '',
  password: '',
  orderId: '',
  orderEmail: '',
  sentEmail: false,
  isUser: false,
  registerForm: {
    displayName: '',
    email: '',
    username: '',
    shortBio: '',
    isAllAgree: false,
    isAdult: false,
    isTerms: false,
    isPrivacy: false,
    isPromotion: false,
  },
  resetForm: {
    password1: '',
    password2: '',
  },
  isSocial: false,
  registerToken: '',
  authResult: null,
  socialAuthResult: null,
  verifySocialResult: null,
  error: null,
  nextUrl: null,
  tokenData: {
    type: null,
    token: null,
  },
};

/* REDUCER */
const reducer = handleActions(
  {
    [SET_EMAIL_INPUT]: (state, action: SetEmailInputAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.email = action.payload;
      });
    },
    [SET_PASSWORD_INPUT]: (state, action: SetPasswordInputAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.password = action.payload;
      });
    },
    [SET_ORDER_ID_INPUT]: (state, action: SetOrderIdInputAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.orderId = action.payload;
      });
    },
    [SET_ORDER_EMAIL_INPUT]: (state, action: SetOrderEmailInputAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.orderEmail = action.payload;
      });
    },
    [RETRY_AUTH_EMAIL]: (state, action: ReTryAuthEmailAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.email = '';
        draft.sentEmail = false;
        draft.isUser = false;
      });
    },
    [CHANGE_REGISTER_FORM]: (state, action: ChangeRegisterFormAction) => {
      const {
        payload: { name, value },
      } = action;
      return produce(state, draft => {
        draft.registerForm[name] = value;
      });
    },
    [CHANGE_RESET_FORM]: (state, action: ChangeResetFormAction) => {
      const {
        payload: { name, value },
      } = action;
      return produce(state, draft => {
        draft.resetForm[name] = value;
      });
    },
    [AUTOCOMPLETE_REGISTER_FORM]: (state, action: AutoCompleteRegisterForm) => {
      const { email, name } = action.payload;
      return produce(state, draft => {
        draft.registerForm = {
          displayName: name,
          email,
          shortBio: '',
          username: '',
        };
        draft.isSocial = true;
      });
    },
    [SET_ERROR]: (state, { payload }: SetErrorAction) => {
      return produce(state, draft => {
        draft.error = payload;
      });
    },
    [SET_NEXT_URL]: (state, { payload }: SetNextUrlAction) => {
      return {
        ...state,
        nextUrl: payload,
      };
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: EMAIL_LOGIN,
    onSuccess: (state: Auth, { payload: { data } }) => {
      return produce(state, draft => {
        draft.isUser = data.isUser;
      });
    },
  },
  {
    type: SEND_AUTH_EMAIL,
    onSuccess: (state: Auth, { payload: { data } }) => {
      return produce(state, draft => {
        draft.sentEmail = true;
        draft.isUser = data.isUser;
      });
    },
  },
  {
    type: GET_CODE,
    onSuccess: (state: Auth, { payload: { data } }) => {
      const { email, registerToken } = data;
      return produce(state, draft => {
        draft.registerForm.email = email;
        draft.registerToken = registerToken;
      });
    },
  },
  {
    type: LOCAL_REGISTER,
    onSuccess: (state: Auth, { payload: { data } }) => {
      const { user, token } = data;
      return produce(state, draft => {
        draft.authResult = {
          user,
          token,
        };
      });
    },
    onFailure: (state: Auth, { payload: { response } }) => {
      return produce(state, draft => {
        draft.error = response.data;
      });
    },
  },
  {
    type: CODE_LOGIN,
    onSuccess: (state: Auth, { payload: { data } }) => {
      const { user, token } = data;
      return produce(state, draft => {
        draft.authResult = {
          user,
          token,
        };
      });
    },
  },
  {
    type: VERIFY_SOCIAL,
    onSuccess: (state: Auth, { payload: response }) => {
      const { profile, exists } = response.data;
      const { id, thumbnail, email, name } = profile;
      return produce(state, draft => {
        draft.verifySocialResult = {
          id,
          thumbnail,
          email,
          name,
          exists,
        };
      });
    },
  },
  {
    type: SOCIAL_LOGIN,
    onSuccess: (state: Auth, { payload: { data } }) => {
      const { user, token } = data;
      return produce(state, draft => {
        draft.authResult = {
          user,
          token,
        };
      });
    },
  },
  {
    type: SOCIAL_REGISTER,
    onSuccess: (state: Auth, { payload: { data } }) => {
      const { user, token } = data;
      return produce(state, draft => {
        draft.authResult = {
          user,
          token,
        };
      });
    },
    onFailure: (state: Auth, { payload: { response } }) => {
      return produce(state, draft => {
        draft.error = response.data;
      });
    },
  },
  {
    type: GET_PROVIDER_TOKEN,
    onSuccess: (state: Auth, { payload: { data }, meta }: GetProviderTokenResponseAction) => {
      const { token } = data;
      // return {
      //   ...state,
      //   providerToken: token,
      // };
      return produce(state, draft => {
        draft.socialAuthResult = {
          accessToken: token,
          provider: meta,
        };
        draft.tokenData = {
          type: meta,
          token,
        };
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/auth.js